/**
 * Origem do cadastro (atribuição de marketing), docs/features/signup-attribution/README.md
 *
 * Na entrada do site guarda de onde a pessoa veio (utm_*, gclid/gbraid/wbraid, site
 * externo e página de entrada) para mandar junto com o cadastro (RegisterAPI).
 * First-party: nada sai para terceiros; o backend valida e trunca tudo de novo.
 *
 * - primeiro toque: a primeira entrada registrada, nunca sobrescrita;
 * - último toque: a entrada mais recente que trouxe utm ou id de clique;
 * - expira em 60 dias contados do primeiro toque.
 *
 * Consentimento: mensurar campanhas é finalidade de "marketing" na política de
 * privacidade, então só persiste em localStorage com "Aceitar todos"
 * (gabriela_cookie_consent = 'all'). Antes da escolha, a entrada fica só em memória
 * e é gravada se a pessoa aceitar nesta mesma visita; "Somente necessários" apaga.
 */
import { getCookieConsent } from '../components/cookieConsent/cookieConsentStorage';

export const SIGNUP_ATTRIBUTION_KEY = 'gabriela_signup_attribution';
const MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;
const PARAM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid'];
const MAX_VALUE_LENGTH = 255;

// Links curtos (?ref=) que viram utm na entrada: a URL com utm_* inteira assusta numa
// mensagem de WhatsApp. a/b = versões do teste A/B da Prospecção (Admin Geral);
// c = o retorno para quem não respondeu.
const SHORT_REFS = {
  a: { utm_source: 'prospeccao', utm_medium: 'whatsapp', utm_campaign: 'prospeccao_conversa' },
  b: { utm_source: 'prospeccao', utm_medium: 'whatsapp', utm_campaign: 'prospeccao_link' },
  c: { utm_source: 'prospeccao', utm_medium: 'whatsapp', utm_campaign: 'prospeccao_retorno' },
};

// Entrada desta visita, aguardando o consentimento
let pendingTouch = null;

const isPatientRoute = (pathname) =>
  typeof window !== 'undefined' &&
  typeof window.gabrielaIsPatientRoute === 'function' &&
  window.gabrielaIsPatientRoute(pathname);

const hasMarketingConsent = () => getCookieConsent() === 'all';

const clip = (value) => String(value).trim().slice(0, MAX_VALUE_LENGTH);

/** Site externo de onde veio, sem query string (pode ter dado pessoal) */
const externalReferrer = (referrer, currentHost) => {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    if (url.hostname === currentHost) return null;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return clip(`${url.protocol}//${url.hostname}${url.pathname}`);
  } catch (e) {
    return null;
  }
};

const hasCampaign = (touch) => PARAM_KEYS.some((key) => touch[key]);

const readStored = () => {
  try {
    const raw = localStorage.getItem(SIGNUP_ATTRIBUTION_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    const firstSeen = stored?.first?.seen_at;
    if (!firstSeen || Date.now() - firstSeen > MAX_AGE_MS) {
      localStorage.removeItem(SIGNUP_ATTRIBUTION_KEY);
      return null;
    }
    return stored;
  } catch (e) {
    return null;
  }
};

const writeStored = (value) => {
  try {
    localStorage.setItem(SIGNUP_ATTRIBUTION_KEY, JSON.stringify(value));
  } catch (e) {
    // localStorage indisponível (modo privado etc.): segue sem origem
  }
};

const mergeTouch = (touch) => {
  if (!touch) return;
  const stored = readStored();
  if (!stored) {
    writeStored({ first: touch, last: touch });
  } else if (hasCampaign(touch)) {
    writeStored({ ...stored, last: touch });
  }
};

/**
 * Lê a URL de entrada uma vez por carregamento da página (src/index.js).
 * Ignora as páginas usadas por pacientes (agendamento, confirmação, avaliação, cadastro).
 */
export function captureSignupAttribution() {
  if (typeof window === 'undefined') return;
  try {
    const { pathname, search, hostname } = window.location;
    if (isPatientRoute(pathname)) return;

    const params = new URLSearchParams(search);
    const touch = { landing_path: clip(pathname || '/'), seen_at: Date.now() };
    PARAM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) touch[key] = clip(value);
    });
    const shortRef = SHORT_REFS[String(params.get('ref') || '').toLowerCase()];
    if (shortRef && !hasCampaign(touch)) Object.assign(touch, shortRef);
    const referrer = externalReferrer(document.referrer, hostname);
    if (referrer) touch.referrer = referrer;

    if (hasMarketingConsent()) {
      mergeTouch(touch);
    } else {
      pendingTouch = touch;
    }
  } catch (e) {
    // Atribuição nunca pode quebrar a entrada no site
  }
}

/** Chamado quando a pessoa clica em "Aceitar todos" no aviso de cookies */
export function persistPendingSignupAttribution() {
  mergeTouch(pendingTouch);
  pendingTouch = null;
}

/** Chamado em "Somente necessários": apaga a origem guardada */
export function clearSignupAttribution() {
  pendingTouch = null;
  try {
    localStorage.removeItem(SIGNUP_ATTRIBUTION_KEY);
  } catch (e) {
    // localStorage indisponível
  }
}

/**
 * Origem a enviar com o cadastro ({ first, last }) ou null sem consentimento/sem dado.
 */
export function getSignupAttribution() {
  if (!hasMarketingConsent()) return null;
  return readStored();
}
